// ─── 5-Star trend aggregation ────────────────────────────────────────────────
//
// Two pure functions behind the scorecard chart, in their own module so the rule
// that matters can be tested rather than eyeballed.
//
// ── THE RULE ────────────────────────────────────────────────────────────────
//
// A GAP IS NOT A STRAIGHT LINE. If CMS published no staffing rating for a quarter,
// joining the quarter before to the quarter after draws a rating nobody ever
// reported, and shows a smooth trend through a period we know nothing about. So
// `segmentsFor` breaks the line at every missing reading instead of interpolating,
// and the caller names the gaps under the chart.
//
// And WITHIN a period, the latest reading wins rather than the average. A quarter
// that read 2 and then 3 is a quarter that ended at 3; 2.5 is a number CMS never
// published.

/** Which bucket a "YYYY-MM" key falls in, at the chosen granularity. */
export function bucketOf(monthKey, granularity) {
  const [y, m] = String(monthKey).split("-");
  if (granularity === "year") return { key: y, label: y };
  if (granularity === "quarter") {
    const q = Math.floor((Number(m) - 1) / 3) + 1;
    return { key: `${y}-Q${q}`, label: `Q${q} ${y}` };
  }
  return {
    key: monthKey,
    label: new Date(`${monthKey}-01T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" }),
  };
}

/**
 * Collapse monthly points into periods, newest reading per period.
 *
 * `points` are ascending `{ monthKey, date, metrics }`. `yearFilter` is a four-digit
 * year or "__all__".
 */
export function aggregateScorecard(points, granularity, yearFilter = "__all__") {
  const inYear = yearFilter === "__all__"
    ? (points || [])
    : (points || []).filter((p) => String(p.monthKey).startsWith(yearFilter));

  const byBucket = new Map();
  inYear.forEach((p) => {
    const b = bucketOf(p.monthKey, granularity);
    const prev = byBucket.get(b.key);
    // LATEST WINS. Not the average — see the header.
    if (!prev || p.date > prev.date) byBucket.set(b.key, { ...b, date: p.date, metrics: p.metrics });
  });
  return Array.from(byBucket.values()).sort((a, b) => a.key.localeCompare(b.key));
}

/**
 * The drawable segments for one metric across a series.
 *
 * Returns an array of arrays of `{ i, v }`. Two adjacent readings with a missing one
 * between them land in DIFFERENT segments, so the chart cannot join across a period
 * CMS did not publish.
 */
export function segmentsFor(series, key) {
  const segments = [];
  let current = [];
  (series || []).forEach((p, i) => {
    const v = p?.metrics?.[key];
    if (v == null || !(v > 0)) {
      if (current.length) segments.push(current);
      current = [];
    } else {
      current.push({ i, v });
    }
  });
  if (current.length) segments.push(current);
  return segments;
}

/** The periods where a metric has no reading, for the note under the chart. */
export function gapsFor(series, metrics) {
  const out = [];
  (metrics || []).forEach((m) => {
    const periods = (series || [])
      .filter((p) => p?.metrics?.[m.key] == null || !(p.metrics[m.key] > 0))
      .map((p) => p.label);
    if (periods.length) out.push({ label: m.label, periods });
  });
  return out;
}
